import { useState } from 'react'
import { v4 as uuid } from 'uuid'
import { CATEGORIAS_MODELO } from '../../data/modelos'
import { CORES, GRUPOS_CORES } from '../../data/cores'
import {
  ADICIONAIS_EXTRAS, ADICIONAIS_QUANTIDADE, ADICIONAIS_DEFAULT,
  PUXADOR_TIPOS, PUXADOR_CORES, PUXADOR_CORES_PERFIL,
  PUXADOR_TIPOS_SEM_COR, PUXADOR_TIPOS_SEM_QTD,
  BASES, inferirBase, expandirBaseEmFlags,
} from '../../data/adicionais'
import { Check } from 'lucide-react'
import { ModeloCombobox } from './ModeloCombobox'

const MOVEL_DEFAULT = {
  id:           '',
  modelo:       '',
  cor:          '',
  comprimento:  '',
  comprimento2: '',
  altura:       '',
  profundidade: '',
  preco:        '',
  adicionais:   { ...ADICIONAIS_DEFAULT },
  observacoes:  '',
}

const MODELOS_DUPLO_COMPRIMENTO = ['Mesa em L', 'Mesa em L com gaveteiro']

// Formata número para exibição com separador de milhar (ex: 1500.5 → "1.500,50")
function formatarPreco(raw) {
  // raw pode ser string digitada ou número existente
  if (raw === '' || raw === null || raw === undefined) return ''
  if (typeof raw === 'number') {
    return raw.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  // Remove tudo exceto dígitos e vírgula (separador decimal BR)
  const limpo = String(raw).replace(/[^\d,]/g, '')
  const [intParte, ...decPartes] = limpo.split(',')
  const intFormatado = intParte.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  return decPartes.length > 0
    ? `${intFormatado},${decPartes.join('').slice(0, 2)}`
    : intFormatado
}

// Converte string formatada para número (ex: "1.500,50" → 1500.50)
function parsearPreco(valor) {
  if (!valor) return 0
  return Number(String(valor).replace(/\./g, '').replace(',', '.')) || 0
}

function Field({ label, error, children, required }) {
  return (
    <div>
      <label className="label">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="error-msg">{error}</p>}
    </div>
  )
}

export function PainelMovel({ inicial, onSalvar, onCancelar }) {
  const [movel, setMovel] = useState(() => {
    if (!inicial) return { ...MOVEL_DEFAULT, id: uuid() }
    const adicionaisMerge = { ...ADICIONAIS_DEFAULT, ...inicial.adicionais }
    // Edição de móvel antigo: infere `base` das flags soltas pra UI nova
    // mostrar a opção certa selecionada.
    adicionaisMerge.base = adicionaisMerge.base || inferirBase(adicionaisMerge)
    return { ...MOVEL_DEFAULT, ...inicial, preco: formatarPreco(inicial.preco), adicionais: adicionaisMerge }
  })
  const [erros, setErros]                   = useState({})
  const [categoriaAberta, setCatAberta]     = useState(null)
  const corEhPadrao = CORES.some(c => c.nome === (inicial?.cor || ''))
  const [grupoCorAberto, setGrupoCorAberto] = useState(
    inicial?.cor && !corEhPadrao ? '__personalizada__' : 'Sólidos'
  )
  const [modoPersonalizado, setModoPersonalizado] = useState(
    inicial?.modelo && !Object.values(CATEGORIAS_MODELO).flat().includes(inicial?.modelo)
  )

  function set(field, value) {
    setMovel(prev => ({ ...prev, [field]: value }))
    setErros(prev => ({ ...prev, [field]: undefined }))
  }

  function setAdicionais(key, value) {
    setMovel(prev => ({ ...prev, adicionais: { ...prev.adicionais, [key]: value } }))
  }

  const isDuploComprimento = MODELOS_DUPLO_COMPRIMENTO.includes(movel.modelo)

  function validar() {
    const e = {}
    if (!movel.modelo)                            e.modelo      = 'Selecione um modelo'
    if (!movel.cor)                               e.cor         = 'Selecione uma cor'
    if (!modoPersonalizado) {
      if (!movel.comprimento || Number(movel.comprimento) <= 0) e.comprimento = 'Obrigatório'
      if (isDuploComprimento && (!movel.comprimento2 || Number(movel.comprimento2) <= 0)) e.comprimento2 = 'Obrigatório'
      if (!movel.altura      || Number(movel.altura)      <= 0) e.altura      = 'Obrigatório'
      if (!movel.profundidade|| Number(movel.profundidade)<= 0) e.profundidade= 'Obrigatório'
    }
    if (!movel.preco       || parsearPreco(movel.preco)  <= 0) e.preco       = 'Informe o preço'

    // Puxador obrigatório — pode ser "Sem puxador", mas precisa escolher
    if (!movel.adicionais.puxadorTipo) {
      e.puxadorTipo = 'Selecione o tipo (use "Sem puxador" se não tiver)'
    } else {
      // Se tipo exige cor, cor é obrigatória
      const exigeCor = !PUXADOR_TIPOS_SEM_COR.includes(movel.adicionais.puxadorTipo)
      if (exigeCor && !movel.adicionais.puxadorCor) {
        e.puxadorCor = 'Selecione a cor do puxador'
      }
    }

    // Base obrigatória
    if (!movel.adicionais.base) e.base = 'Selecione a base do móvel'

    // Adicionais: precisa OU marcar "Sem adicionais" OU pelo menos uma das opções
    // (Moldura/Espessura) OU uma quantidade > 0 (gavetas, chaves, prateleiras)
    const temAlgumExtra = ADICIONAIS_EXTRAS.some(({ key }) => movel.adicionais[key])
    const temAlgumaQtd  = ADICIONAIS_QUANTIDADE.some(({ key }) => Number(movel.adicionais[key]) > 0)
    if (!movel.adicionais.semAdicionais && !temAlgumExtra && !temAlgumaQtd) {
      e.adicionais = 'Marque "Sem adicionais" ou selecione um adicional'
    }

    setErros(e)
    return Object.keys(e).length === 0
  }

  function handleSalvar() {
    if (!validar()) return
    // Expande a `base` selecionada nas flags antigas pra compat
    const adicionaisFinal = expandirBaseEmFlags(movel.adicionais)
    onSalvar({ ...movel, adicionais: adicionaisFinal, preco: parsearPreco(movel.preco) })
  }

  const corSelecionada = CORES.find(c => c.nome === movel.cor)

  return (
    <div className="space-y-6">

      {/* ── Modelo ──────────────────────────────────────────────────────── */}
      <Field label="Modelo do móvel" required error={erros.modelo}>

        {modoPersonalizado ? (
          /* Modo personalizado: textarea sempre editável */
          <div className="space-y-2">
            <textarea
              autoFocus rows={3}
              value={movel.modelo}
              onChange={e => set('modelo', e.target.value)}
              className="input resize-none text-sm"
              placeholder="Descreva o móvel personalizado: ex. Estante sob medida com 3 portas de correr em MDF 18mm, trilho embutido, nichos internos..."
            />
            <button type="button" onClick={() => { set('modelo', ''); setModoPersonalizado(false) }}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
              ← voltar para lista de modelos
            </button>
          </div>
        ) : (
          <ModeloCombobox
            value={movel.modelo}
            onChange={v => { set('modelo', v) }}
            onPersonalizado={() => setModoPersonalizado(true)}
          />
        )}
      </Field>

      {/* ── Cor ─────────────────────────────────────────────────────────── */}
      <Field label="Cor" required error={erros.cor}>
        {/* Group tabs */}
        <div className="flex gap-1 mb-3 flex-wrap">
          {Object.keys(GRUPOS_CORES).map(grupo => (
            <button
              key={grupo}
              type="button"
              onClick={() => {
                setGrupoCorAberto(grupo)
                // Se estava em personalizada, limpa a cor para forçar nova seleção
                if (grupoCorAberto === '__personalizada__') set('cor', '')
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${grupoCorAberto === grupo
                  ? 'bg-[#111111] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {grupo}
            </button>
          ))}
          <button
            type="button"
            onClick={() => { setGrupoCorAberto('__personalizada__'); set('cor', '') }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
              ${grupoCorAberto === '__personalizada__'
                ? 'bg-[#111111] text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Personalizada
          </button>
        </div>

        {grupoCorAberto === '__personalizada__' ? (
          <div>
            <input
              type="text"
              autoFocus
              value={movel.cor}
              onChange={e => set('cor', e.target.value)}
              className="input"
              placeholder="Ex: Verde Musgo, Rosa Bebê, Amadeirado Carvalho…"
            />
            <p className="text-xs text-slate-400 mt-1.5">
              Digite o nome exato da cor conforme o catálogo do fornecedor.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {GRUPOS_CORES[grupoCorAberto].map(nomeCor => {
              const cor   = CORES.find(c => c.nome === nomeCor)
              const ativa = movel.cor === nomeCor
              if (!cor) return null
              return (
                <button
                  key={nomeCor}
                  type="button"
                  onClick={() => set('cor', nomeCor)}
                  title={nomeCor}
                  className={`relative flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all
                    ${ativa
                      ? 'border-[#111111] shadow-md scale-105'
                      : 'border-slate-200 hover:border-slate-400'}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg shadow-inner border border-black/10 relative"
                    style={{ backgroundColor: cor.hex }}
                  >
                    {ativa && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Check
                          size={16}
                          className={cor.textDark ? 'text-[#111111]' : 'text-white'}
                          strokeWidth={3}
                        />
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-600 leading-tight text-center font-medium">
                    {nomeCor}
                  </span>
                </button>
              )
            })}
          </div>
        )}

        {corSelecionada && grupoCorAberto !== '__personalizada__' && (
          <p className="text-xs text-slate-500 mt-2">
            Selecionada: <strong className="text-[#111111]">{corSelecionada.nome}</strong>
            <span className="ml-1.5 text-slate-400">({corSelecionada.tipo})</span>
          </p>
        )}
      </Field>

      {/* ── Medidas ─────────────────────────────────────────────────────── */}
      <div>
        <p className="label">
          Medidas (cm)
          {modoPersonalizado
            ? <span className="text-slate-400 font-normal ml-1">(opcional)</span>
            : <span className="text-red-500"> *</span>}
        </p>
        <div className={`grid gap-3 ${isDuploComprimento ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3'}`}>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">
              {isDuploComprimento ? 'Comprimento lado A' : 'Comprimento'}
            </label>
            <input
              type="number" min="1" step="0.1"
              value={movel.comprimento}
              onChange={e => set('comprimento', e.target.value)}
              className={`input text-center ${erros.comprimento ? 'input-error' : ''}`}
              placeholder="0"
            />
            {erros.comprimento && <p className="error-msg text-center">{erros.comprimento}</p>}
          </div>

          {isDuploComprimento && (
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Comprimento lado B</label>
              <input
                type="number" min="1" step="0.1"
                value={movel.comprimento2}
                onChange={e => set('comprimento2', e.target.value)}
                className={`input text-center ${erros.comprimento2 ? 'input-error' : ''}`}
                placeholder="0"
              />
              {erros.comprimento2 && <p className="error-msg text-center">{erros.comprimento2}</p>}
            </div>
          )}

          {[
            { key: 'profundidade',label: 'Profundidade' },
            { key: 'altura',      label: 'Altura'       },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="text-xs text-slate-500 mb-1 block">{label}</label>
              <input
                type="number" min="1" step="0.1"
                value={movel[key]}
                onChange={e => set(key, e.target.value)}
                className={`input text-center ${erros[key] ? 'input-error' : ''}`}
                placeholder="0"
              />
              {erros[key] && <p className="error-msg text-center">{erros[key]}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* ── Preço ───────────────────────────────────────────────────────── */}
      <Field label="Preço (R$)" required error={erros.preco}>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">R$</span>
          <input
            type="text"
            inputMode="numeric"
            value={movel.preco}
            onChange={e => set('preco', formatarPreco(e.target.value))}
            className={`input pl-9 ${erros.preco ? 'input-error' : ''}`}
            placeholder="0,00"
          />
        </div>
      </Field>

      {/* ── Puxador ─────────────────────────────────────────────────────── */}
      <div className={`border rounded-xl p-4 space-y-3 ${erros.puxadorTipo || erros.puxadorCor ? 'border-red-300 bg-red-50/20' : 'border-slate-200'}`}>
        <div className="flex items-center justify-between">
          <p className="label mb-0">
            Puxador <span className="text-red-500">*</span>
          </p>
          {!movel.adicionais.puxadorTipo && (
            <p className="text-xs text-slate-500 italic">Clique em "Sem puxador" se a peça não tiver</p>
          )}
        </div>

        {/* Tipo */}
        <div>
          <p className="text-xs text-slate-500 mb-2 font-medium">Tipo</p>
          <div className="flex flex-wrap gap-2">
            {PUXADOR_TIPOS.map(tipo => {
              const ativo = movel.adicionais.puxadorTipo === tipo
              const ehSemPuxador = tipo === 'Sem puxador'
              return (
                <button
                  key={tipo}
                  type="button"
                  onClick={() => {
                    // "Sem puxador" zera cor e qtd. Outros mantêm.
                    const novoTipo = ativo ? '' : tipo
                    const semCor = PUXADOR_TIPOS_SEM_COR.includes(novoTipo)
                    const semQtd = PUXADOR_TIPOS_SEM_QTD.includes(novoTipo)
                    setMovel(prev => ({
                      ...prev,
                      adicionais: {
                        ...prev.adicionais,
                        puxadorTipo: novoTipo,
                        puxadorCor: semCor ? '' : prev.adicionais.puxadorCor,
                        puxadorQtd: semQtd ? 0 : prev.adicionais.puxadorQtd,
                      }
                    }))
                    setErros(prev => ({ ...prev, puxadorTipo: undefined, puxadorCor: undefined }))
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm border-2 transition-all font-medium
                    ${ativo
                      ? (ehSemPuxador
                        ? 'border-slate-600 bg-slate-600 text-white'
                        : 'border-[#111111] bg-[#111111] text-white')
                      : (ehSemPuxador
                        ? 'border-slate-300 text-slate-500 italic hover:border-slate-500'
                        : 'border-slate-200 text-slate-600 hover:border-slate-400')}`}
                >
                  {tipo}
                </button>
              )
            })}
          </div>
          {erros.puxadorTipo && <p className="error-msg mt-2">{erros.puxadorTipo}</p>}
        </div>

        {/* Cor — só aparece se tipo exige cor. Perfil usa cores próprias. */}
        {movel.adicionais.puxadorTipo && !PUXADOR_TIPOS_SEM_COR.includes(movel.adicionais.puxadorTipo) && (
          <div>
            <p className="text-xs text-slate-500 mb-2 font-medium">
              Cor do puxador
              {movel.adicionais.puxadorTipo === 'Perfil' && (
                <span className="text-slate-400 font-normal ml-1">(cores do Perfil)</span>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {(movel.adicionais.puxadorTipo === 'Perfil' ? PUXADOR_CORES_PERFIL : PUXADOR_CORES).map(cor => {
                const ativa = movel.adicionais.puxadorCor === cor.nome
                return (
                  <button
                    key={cor.nome}
                    type="button"
                    onClick={() => { setAdicionais('puxadorCor', ativa ? '' : cor.nome); setErros(prev => ({ ...prev, puxadorCor: undefined })) }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border-2 transition-all
                      ${ativa
                        ? 'border-[#F5C200] bg-[#F5C200]/10 font-semibold'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  >
                    <span
                      className="w-4 h-4 rounded-full border border-black/20 shrink-0"
                      style={{ backgroundColor: cor.hex }}
                    />
                    {cor.nome}
                  </button>
                )
              })}
            </div>
            {erros.puxadorCor && <p className="error-msg mt-2">{erros.puxadorCor}</p>}
          </div>
        )}

        {/* Quantidade */}
        {movel.adicionais.puxadorTipo && !PUXADOR_TIPOS_SEM_QTD.includes(movel.adicionais.puxadorTipo) && (
          <div className="flex items-center gap-3">
            <p className="text-xs text-slate-500 font-medium flex-1">Quantidade</p>
            <div className="flex items-center gap-2">
              <button type="button"
                onClick={() => setAdicionais('puxadorQtd', Math.max(0, (movel.adicionais.puxadorQtd || 0) - 1))}
                className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold flex items-center justify-center transition-colors"
              >−</button>
              <span className="w-8 text-center font-bold text-[#111111]">
                {movel.adicionais.puxadorQtd || 0}
              </span>
              <button type="button"
                onClick={() => setAdicionais('puxadorQtd', (movel.adicionais.puxadorQtd || 0) + 1)}
                className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold flex items-center justify-center transition-colors"
              >+</button>
            </div>
          </div>
        )}

        {movel.adicionais.puxadorTipo === 'Sem puxador' && (
          <p className="text-xs text-slate-500 italic">✓ Sem puxador — peça sem ferragem de manuseio</p>
        )}
      </div>

      {/* ── Base ────────────────────────────────────────────────────────── */}
      <div className={`border rounded-xl p-4 space-y-3 ${erros.base ? 'border-red-300 bg-red-50/20' : 'border-slate-200'}`}>
        <p className="label mb-0">
          Base <span className="text-red-500">*</span>
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {BASES.map(b => {
            const ativa = movel.adicionais.base === b.id
            const ehSemBase = b.id.startsWith('sem')
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => { setAdicionais('base', b.id); setErros(prev => ({ ...prev, base: undefined })) }}
                className={`px-3 py-2.5 rounded-lg text-sm border-2 transition-all font-medium text-left
                  ${ativa
                    ? (ehSemBase
                      ? 'border-slate-600 bg-slate-600 text-white'
                      : 'border-[#111111] bg-[#111111] text-white')
                    : (ehSemBase
                      ? 'border-slate-300 text-slate-500 italic hover:border-slate-500'
                      : 'border-slate-200 text-slate-600 hover:border-slate-400')}`}
              >
                {b.label}
              </button>
            )
          })}
        </div>
        {erros.base && <p className="error-msg">{erros.base}</p>}
      </div>

      {/* ── Adicionais ──────────────────────────────────────────────────── */}
      {/* Todas as 3 opções na mesma grid: "Sem adicionais", "Moldura",
          "Espessura 25 mm". "Sem adicionais" é mutuamente exclusivo com
          as outras duas — clicar nele desmarca Moldura/Espessura. Marcar
          Moldura ou Espessura desmarca "Sem adicionais". Validar que ao
          menos uma está marcada pra forçar escolha explícita. */}
      <div className={`border rounded-xl p-4 space-y-3 ${erros.adicionais ? 'border-red-300 bg-red-50/20' : 'border-slate-200'}`}>
        <p className="label mb-0">
          Adicionais <span className="text-red-500">*</span>
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {/* Sem adicionais — opção em pé de igualdade com as outras,
              mas com estilo italic cinza pra deixar claro que é "nenhum".
              Marcar "Sem adicionais" zera moldura/espessura E todas as
              quantidades (gavetas, chaves, prateleiras). */}
          <button
            type="button"
            onClick={() => {
              setMovel(prev => {
                const adicionais = {
                  ...prev.adicionais,
                  semAdicionais: true,
                  moldura:       false,
                  espessura25mm: false,
                }
                // Zera todas as quantidades também
                ADICIONAIS_QUANTIDADE.forEach(({ key }) => { adicionais[key] = 0 })
                return { ...prev, adicionais }
              })
              setErros(prev => ({ ...prev, adicionais: undefined }))
            }}
            className={`px-3 py-2.5 rounded-lg text-sm border-2 transition-all font-medium text-left
              ${movel.adicionais.semAdicionais
                ? 'border-slate-600 bg-slate-600 text-white'
                : 'border-slate-300 text-slate-500 italic hover:border-slate-500'}`}
          >
            Sem adicionais
          </button>

          {ADICIONAIS_EXTRAS.map(({ key, label }) => {
            const ativo = movel.adicionais[key]
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setMovel(prev => ({
                    ...prev,
                    adicionais: {
                      ...prev.adicionais,
                      [key]: !ativo,
                      // Marcar um adicional desliga "Sem adicionais"
                      semAdicionais: !ativo ? false : prev.adicionais.semAdicionais,
                    }
                  }))
                  setErros(prev => ({ ...prev, adicionais: undefined }))
                }}
                className={`px-3 py-2.5 rounded-lg text-sm border-2 transition-all font-medium text-left
                  ${ativo
                    ? 'border-[#111111] bg-[#111111] text-white'
                    : 'border-slate-200 text-slate-600 hover:border-slate-400'}`}
              >
                {label}
              </button>
            )
          })}
        </div>

        {/* Divisor + bloco de quantidades dentro do MESMO card de Adicionais.
            Marcar quantidade > 0 desliga "Sem adicionais" automaticamente. */}
        <div className="pt-3 mt-1 border-t border-slate-200">
          <p className="text-xs text-slate-500 mb-2 font-medium">Quantidades</p>
          <div className="grid grid-cols-2 gap-2">
            {ADICIONAIS_QUANTIDADE.map(({ key, label }) => (
              <div
                key={key}
                className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 border transition-colors
                  ${movel.adicionais[key] > 0
                    ? 'bg-[#F5C200]/10 border-[#F5C200]/40'
                    : 'bg-white border-slate-200'}`}
              >
                <span className="text-sm text-slate-700 flex-1 leading-tight">{label}</span>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button"
                    onClick={() => {
                      const novoQtd = Math.max(0, (movel.adicionais[key] || 0) - 1)
                      setAdicionais(key, novoQtd)
                    }}
                    className="w-6 h-6 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm flex items-center justify-center transition-colors"
                  >−</button>
                  <span className="w-6 text-center text-sm font-bold text-[#111111]">
                    {movel.adicionais[key] || 0}
                  </span>
                  <button type="button"
                    onClick={() => {
                      const novoQtd = (movel.adicionais[key] || 0) + 1
                      // Marcar quantidade desliga "Sem adicionais"
                      setMovel(prev => ({
                        ...prev,
                        adicionais: {
                          ...prev.adicionais,
                          [key]: novoQtd,
                          semAdicionais: false,
                        }
                      }))
                      setErros(prev => ({ ...prev, adicionais: undefined }))
                    }}
                    className="w-6 h-6 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-sm flex items-center justify-center transition-colors"
                  >+</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {erros.adicionais && <p className="error-msg">{erros.adicionais}</p>}
      </div>

      {/* ── Observações ─────────────────────────────────────────────────── */}
      <div>
        <label className="label">Observações específicas do produto</label>
        <textarea
          value={movel.observacoes}
          onChange={e => set('observacoes', e.target.value)}
          rows={3}
          className="input resize-none"
          placeholder="Detalhes especiais, referências, pedidos customizados..."
        />
      </div>

      {/* ── Ações ───────────────────────────────────────────────────────── */}
      <div className="flex gap-3 pt-2 border-t border-slate-100">
        <button type="button" onClick={onCancelar} className="btn-secondary flex-1 justify-center">
          Cancelar
        </button>
        <button type="button" onClick={handleSalvar} className="btn-dark flex-1 justify-center">
          {inicial ? 'Salvar alterações' : 'Adicionar móvel'}
        </button>
      </div>
    </div>
  )
}
